import { Sprout, Truck, GraduationCap } from "lucide-react";

const pillars = [
  {
    num: "01",
    icon: Sprout,
    title: "Climate-adapted community gardens",
    description:
      "Designed for desert heat, tropical wet, and coastal salt — local food production capability built with communities and owned by communities.",
    image: "https://customer-assets.emergentagent.com/job_country-gardens/artifacts/nb6cg1vw_garden.JPG",
    alt: "Lush community garden on Country — pumpkins, brassicas and squash growing in red earth with greenhouse and water tank in the background",
  },
  {
    num: "02",
    icon: Truck,
    title: "Mobile horticultural support",
    description:
      "A fleet of fully-equipped growing vehicles delivering technical expertise, propagation, seed banking and seasonal training directly into the communities that need it most.",
    image:
      "https://customer-assets.emergentagent.com/job_country-gardens/artifacts/humop34h_vehicle%20only.png",
    alt: "Growing Country FUSO mobile horticulture unit on red-earth Country at golden hour — branded with Growing Country logo, fully kitted with tools, nursery shelves and satellite comms",
  },
  {
    num: "03",
    icon: GraduationCap,
    title: "Practical training pathways",
    description:
      "Hands-on, accredited training in horticulture, irrigation, ranger-led land care and small-scale food production — opening real employment pathways for young people on Country.",
    image: "https://customer-assets.emergentagent.com/job_country-gardens/artifacts/kueey1xu_seed%20production%20area.jpeg",
    alt: "Seed production area — red-earth seed beds with drip irrigation, practical horticulture in action",
  },
];

export default function Approach() {
  return (
    <section
      id="approach"
      data-testid="approach-section"
      className="relative bg-stone-alt py-24 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16 md:mb-24">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-sage mb-6">
              Resilience Infrastructure
            </div>
            <h2
              data-testid="approach-heading"
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-charcoal"
            >
              Three connected pillars,
              <span className="block italic text-sage">one resilient system.</span>
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <p className="text-base md:text-lg leading-relaxed text-ink">
              Designed to complement existing food systems while rebuilding
              local production, capability, and community-owned resilience.
            </p>
          </div>
        </div>

        <div className="space-y-24 md:space-y-32">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            const reverse = i % 2 === 1;
            return (
              <div
                key={p.num}
                data-testid={`approach-pillar-${i}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center"
              >
                <div className={`md:col-span-6 ${reverse ? "md:order-2" : ""}`}>
                  <div className="relative">
                    <img
                      src={p.image}
                      alt={p.alt}
                      className="w-full aspect-[4/3] object-cover"
                    />
                    <div
                      className={`absolute -top-6 ${
                        reverse ? "-right-6 md:-right-10" : "-left-6 md:-left-10"
                      } hidden md:block`}
                    >
                      <span className="font-serif text-[140px] leading-none text-rust/15 tick-num select-none">
                        {p.num}
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  className={`md:col-span-5 ${
                    reverse ? "md:order-1 md:col-start-1" : "md:col-start-8"
                  }`}
                >
                  <div className="flex items-center gap-4 mb-6">
                    <Icon className="w-6 h-6 text-rust" strokeWidth={1.5} />
                    <span className="text-xs uppercase tracking-[0.28em] text-rust">
                      Pillar {p.num}
                    </span>
                  </div>
                  <h3 className="font-serif text-3xl md:text-4xl font-light leading-tight text-charcoal">
                    {p.title}
                  </h3>
                  <p className="mt-6 text-base md:text-lg leading-relaxed text-ink">
                    {p.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
